import { AUTH_LOGIN, AUTH_LOGOUT, AUTH_ERROR, AUTH_GET_PERMISSIONS, AUTH_CHECK, useLogout } from "react-admin";
import permissionManager from "./permissionManager";
import * as Sentry from "@sentry/browser";
import { sendSentryException } from "./SentryErrorHandler";

// Store permissions in memory
let cachedPermissions = null;

const fetchPermissions = async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    return Promise.reject("Unauthorized: Token not found");
  }
  const API_URL = process.env.REACT_APP_API_URL;
  const requestUrl = `${API_URL}/internal/administrators/me/permissions`;
  const requestOptions = {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  };

  try {
    const response = await fetch(requestUrl, requestOptions);

    // Check if the response is a 403 or 401
    if (response.status === 403 || response.status === 401) {
      logoutToLogin();
      return Promise.reject();
    }

    const user_permissions = await response.json();
    if (!user_permissions) {
      logoutToLogin();
      return Promise.reject("Invalid permissions response");
    }
    // Handle new granular permissions format
    let granular_permissions = new Map(Object.entries(user_permissions));
    if (user_permissions.email) {
      localStorage.setItem("user_email", user_permissions.email);
      Sentry.setUser({ email: user_permissions.email });
    }
    if (granular_permissions.size > 10) {
      // Store granular permissions
      const granular_permissions_obj = Object.fromEntries(granular_permissions);
      localStorage.setItem("granular_permissions", JSON.stringify(granular_permissions_obj));
      cachedPermissions = new Map(Object.entries(user_permissions));
    }
    // Handle old permissions format (fallback)
    else handlePermissionsFallback(user_permissions);
    return Promise.resolve(cachedPermissions);
  } catch (error) {
    sendSentryException(error, "fetchPermissions");
    return Promise.reject();
  }
};

// `new URL` throws on anything that isn't an absolute URL; an error we can't
// attribute to a host isn't one we want to log the admin out over.
const originOf = (url) => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/*
 * Only our own API knows anything about the admin's session. Everything else we
 * call (HERE routing, for instance) is authenticated by a third party, so its
 * 401s/403s say nothing about whether the admin is still logged in.
 */
export const isPlatformAuthError = (params) => {
  // Without a configured API URL (test builds) there is nothing to compare
  // against, so treat every auth error as coming from us.
  const apiOrigin = originOf(process.env.REACT_APP_API_URL);
  if (!apiOrigin) return true;
  return originOf(params && params.url) === apiOrigin;
};

/*
 * react-admin navigates to `redirectTo` whenever checkError rejects, even when
 * we ask it to keep the user logged in, so send it back to the current route.
 */
export const currentRoute = () => {
  const hash = window.location.hash || "";
  return (hash.startsWith("#") ? hash.slice(1) : hash) || "/";
};

export const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

/*
 * Only a same-document path is safe to hand back to the router: a route
 * starting with `//` (or a scheme) would send the admin off to another site.
 */
const isSafeRoute = (route) => typeof route === "string" && route.startsWith("/") && !route.startsWith("//");

/*
 * A 401 means the session is gone, so we clear everything and bounce to the
 * login page. The route the admin was on has to be read before the storage
 * clear and written back after it, so they land where they left off once they
 * sign back in.
 */
export const logoutToLogin = () => {
  const route = currentRoute();
  cachedPermissions = null;
  permissionManager.deletePerms();
  localStorage.clear();
  sessionStorage.clear();
  if (isSafeRoute(route) && !route.startsWith("/login")) localStorage.setItem(POST_LOGIN_REDIRECT_KEY, route);
  window.location.href = "/#/login";
};

/*
 * Reads and consumes the route stashed by `logoutToLogin`; it is a one-shot, so
 * a later manual visit to the login page doesn't bounce anywhere unexpected.
 */
export const takePostLoginRedirect = () => {
  const route = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return isSafeRoute(route) ? route : null;
};

/*
 * A 403 means the session is valid but the user lacks permission for this
 * request, so surface the reason the API gave us when there is one.
 */
export const permissionDeniedMessage = ({ resource, parsedBody } = {}) => {
  const detail =
    parsedBody && typeof parsedBody === "object"
      ? parsedBody.message || (parsedBody.errors && Object.values(parsedBody.errors).join(" "))
      : null;
  const target = resource ? ` to ${resource}` : "";
  return detail
    ? `Permission denied${target}: ${detail}`
    : `Permission denied: you do not have access${target}. Contact a permissions admin if you need it.`;
};

const handlePermissionsFallback = (user_permissions) => {
  const platform_permissions =
    user_permissions.read_only && !user_permissions.super_admin
      ? "read_only"
      : user_permissions.super_admin
        ? "full"
        : null;
  const billing_permissions = user_permissions.billing_admin ? "full" : null;
  const permissions_management = user_permissions.permissions_admin ? "full" : null;

  permissionManager.setPerms(platform_permissions);
  permissionManager.setBillingPerms(billing_permissions);
  permissionManager.setPermissionsPerms(permissions_management);
  cachedPermissions = permissionManager.getPerms();
};

export default async (type, params) => {
  switch (type) {
    case AUTH_LOGIN:
      localStorage.setItem("token", params.access_token);
      return await fetchPermissions();
    case AUTH_LOGOUT:
      cachedPermissions = null;
      permissionManager.deletePerms();
      sessionStorage.clear();
      localStorage.clear();
      return Promise.resolve();
    case AUTH_ERROR: {
      // Only our own API's auth errors tell us anything about the session
      const isOurApi = isPlatformAuthError(params);

      // A 401 means the session itself is no longer good: log the user out.
      if (params.status === 401 && isOurApi) {
        logoutToLogin();
        return Promise.reject();
      }
      // A 403 means the session is fine, the user just can't do this. Keep them
      // logged in and let react-admin show a red error notification instead.
      if (params.status === 403 && isOurApi) {
        return Promise.reject({
          logoutUser: false,
          redirectTo: currentRoute(),
          message: permissionDeniedMessage(params),
        });
      }
      return Promise.resolve(params.message);
    }
    case AUTH_GET_PERMISSIONS:
      if (cachedPermissions) return Promise.resolve(cachedPermissions);
      return fetchPermissions();
    case AUTH_CHECK:
      return localStorage.getItem("token") ? Promise.resolve() : Promise.reject();
    default:
      return Promise.reject(`Unknown method: ${type}`);
  }
};
